/**
 * Client helpers for AI evidence extraction → intake / matter form fields.
 */

import { api } from './api.js';

export const EVIDENCE_EXTRACT_DISPUTES = Object.freeze([
  'CORPORATE_BUSINESS_DEBT',
  'EMPLOYMENT_LABOR',
  'FAMILY_DOMESTIC',
  'INTELLECTUAL_PROPERTY',
  'PERSONAL_INJURY',
  'REAL_ESTATE',
  'MARITIME_ADMIRALTY',
  'BANKING_FINANCE',
  'CIVIL_RIGHTS',
  'HEALTHCARE',
]);

function cleanString(value, max = 200) {
  const text = String(value || '').trim();
  if (!text) return null;
  return text.slice(0, max);
}

function cleanAmount(value) {
  if (value == null || value === '') return null;
  const raw = String(value).replace(/[^0-9.-]/g, '');
  const num = Number(raw);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Number(num.toFixed(2));
}

/** Form / display string always keeps cents (664 → "664.00"). */
export function formatExtractedAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num.toFixed(2);
}

export function normalizeEvidenceExtraction(raw = {}, source = 'heuristic') {
  const confidence =
    raw.confidence && typeof raw.confidence === 'object' ? { ...raw.confidence } : {};
  const dispute = String(raw.disputeType || raw.Dispute_Type || '')
    .trim()
    .toUpperCase();
  return {
    debtorName: cleanString(raw.debtorName || raw.Debtor_Name),
    debtorCompany: cleanString(raw.debtorCompany || raw.Debtor_Company),
    debtorEmail: cleanString(raw.debtorEmail || raw.Debtor_Email, 120),
    debtorStreet: cleanString(raw.debtorStreet || raw.Debtor_Street, 160),
    debtorCity: cleanString(raw.debtorCity || raw.Debtor_City, 80),
    debtorState: cleanString(raw.debtorState || raw.Debtor_State, 40),
    debtorPostalCode: cleanString(raw.debtorPostalCode || raw.Debtor_Postal_Code, 20),
    debtorCountry: cleanString(raw.debtorCountry || raw.Debtor_Country, 40) || 'US',
    amountOwed: cleanAmount(raw.amountOwed || raw.Amount_Owed),
    externalInvoiceId: cleanString(
      raw.externalInvoiceId || raw.External_Invoice_ID || raw.invoiceId,
      80
    ),
    disputeType: EVIDENCE_EXTRACT_DISPUTES.includes(dispute)
      ? dispute
      : 'CORPORATE_BUSINESS_DEBT',
    confidence,
    summary: cleanString(raw.summary, 400) || '',
    source: String(raw.source || source),
  };
}

const BILL_TO_STOP =
  /\s+(?:ship\s*to|bill\s*to|sold\s*to|invoice\s*#|invoice\b|account\b|date\b|total\b|balance\b|due\s*date|terms\b|activity\b|description\b|qty\b|rate\b|amount\b|email\b|e-?mail\b|phone\b|tel\b|fax\b|ways\s+to\s+pay|pay\s+online|thank\s+you|from\s*:|subject\s*:|notes\s*:|please\s+pay|please\s+remit)\b/i;

const BUSINESS_WORD =
  /capital|holdings|group|partners|services|solutions|technologies|tech|industries|enterprises|international|trading|traders|supplies|supply|logistics|media|studio|agency|bank|clinic|hospital|school|university|foundation|association|imports|outfitters|hotels?|manufacturing|energy|analytics|transit|motors|management|retail|dental|foods?|packaging|components|tools|co-?op|llc|inc|corp|ltd|company|\bco\b/i;

const FILENAME_JUNK =
  /^(filename|csv|email|issuer|customer|contact|formal|minimal|messy|stripe|quickbooks|healthcare|construction|employment|banking|canada|large|due|multi|all|po|ship|remittance|invoice|receipt|statement|evidence|body|ledger|export|style|hints|only|differs|from|and|the|a|an)$/i;

function isPlausibleInvoiceId(value) {
  const text = String(value || '').trim();
  if (!text || text.length < 3) return false;
  if (
    /^(DATE|TOTAL|DUE|TERMS|AMOUNT|BALANCE|ENCLOSED|BODY|MERCY|FROM|SUBJECT|NOTES|ACCOUNT|INVOICE|NUMBER|NO)$/i.test(
      text
    )
  ) {
    return false;
  }
  return /[0-9]/.test(text) || /^INV-/i.test(text);
}

/** Pull the debtor (who owes) from BILL TO / Customer blocks — not the invoice issuer. */
export function parseBillToParty(corpus = '') {
  const text = String(corpus || '');
  // Require "Bill To" / "Sold To" with a space (not filename BillTo_Atlas).
  // "Customer:" needs a colon so filename Customer_Maple does not match.
  const billToMatch =
    text.match(/(?:^|[^A-Za-z0-9])(?:bill\s+to|sold\s+to)\b\s*[:#-]?\s*([\s\S]+)/i) ||
    text.match(/(?:^|[^A-Za-z0-9])customer\s*(?:name)?\s*:\s*([\s\S]+)/i);
  if (!billToMatch) return { company: null, name: null, block: '', blockStart: -1 };
  const blockStart = billToMatch.index ?? 0;
  let block = String(billToMatch[1] || '').trim();
  const stop = block.search(BILL_TO_STOP);
  if (stop >= 0) block = block.slice(0, stop).trim();
  const lines = block
    .split(/\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const firstLine = lines[0] || block.replace(/\s+/g, ' ').trim();
  block = block.replace(/\s+/g, ' ').trim();
  if (!block) return { company: null, name: null, block: '', blockStart };

  const attnMatch = block.match(
    /\b(?:Attn|Attention|ATTN)\s*:?\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\b(?!\s*@)/
  );
  const companyMatch = block.match(
    /\b([A-Z][A-Za-z0-9&.'/-]*(?:\s+[A-Z][A-Za-z0-9&.'/-]*){0,5}\s+(?:LLC|L\.L\.C\.|Inc\.?|Corp\.?|Ltd\.?|Co\.?|Company|LP|LLP|Group|Partners|Analytics|Hotels?|Imports|Outfitters|Manufacturing|Energy|Management|Transit|Motors|Clinic|Hospital|Co-?op))\b/
  );

  const looksLikePerson = (party) =>
    /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+$/.test(party) && !BUSINESS_WORD.test(party);

  if (companyMatch) {
    let company = companyMatch[1].trim();
    let name = attnMatch ? attnMatch[1].trim() : null;
    const leadingPerson = company.match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\s+(.+)$/);
    if (
      !name &&
      leadingPerson &&
      looksLikePerson(leadingPerson[1]) &&
      BUSINESS_WORD.test(leadingPerson[2])
    ) {
      name = leadingPerson[1];
      company = leadingPerson[2].trim();
    }
    if (!name && lines.length >= 2 && looksLikePerson(firstLine) && !company.startsWith(firstLine)) {
      name = firstLine;
    }
    return { company, name, block, blockStart };
  }

  const party = firstLine.replace(/,.*$/, '').trim();
  if (party && !/^(invoice|date|total|amount|balance)$/i.test(party)) {
    if (looksLikePerson(party)) {
      const second = lines[1] || '';
      if (second && !/@/.test(second) && !/^\d/.test(second) && BUSINESS_WORD.test(second)) {
        return { company: second.replace(/,.*$/, '').trim(), name: party, block, blockStart };
      }
      if (second && !/@/.test(second) && !/^\d/.test(second) && !/^(p\.?\s*o\.?\s*box)/i.test(second)) {
        // "Sam Patel" / "Cedar Grove Analytics"
        if (/[A-Z][a-z]+\s+[A-Z]/.test(second) || BUSINESS_WORD.test(second)) {
          return { company: second.replace(/,.*$/, '').trim(), name: party, block, blockStart };
        }
      }
      return { company: null, name: party, block, blockStart };
    }
    return { company: party, name: attnMatch ? attnMatch[1].trim() : null, block, blockStart };
  }
  return { company: null, name: null, block, blockStart };
}

function parseDebtorEmail(corpus = '', billToStart = -1, billToCompany = null) {
  const text = String(corpus || '');
  const emailRe = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  const emails = [];
  let match;
  while ((match = emailRe.exec(text))) {
    emails.push({ email: match[0], index: match.index });
  }
  if (!emails.length) return null;
  // Prefer an email inside/after the BILL TO block; issuer remittance emails sit above it.
  if (billToStart >= 0) {
    const after = emails.find((item) => item.index >= billToStart);
    if (after) return after.email;
    // CSV / ledger rows often list customer + email before a trailing "Bill To" note.
    // Only accept pre-Bill-To emails that share the same line as the debtor company.
    const company = String(billToCompany || '').trim().toLowerCase();
    if (company) {
      const needle = company.slice(0, 12);
      const near = emails.find((item) => {
        if (item.index >= billToStart) return false;
        const lineStart = text.lastIndexOf('\n', item.index) + 1;
        const lineEnd = text.indexOf('\n', item.index);
        const line = text
          .slice(lineStart, lineEnd < 0 ? text.length : lineEnd)
          .toLowerCase();
        const companyPos = needle ? line.indexOf(needle) : -1;
        const emailPos = item.index - lineStart;
        // Customer name should appear before the email on that row (CSV / AP exports).
        return companyPos >= 0 && companyPos < emailPos;
      });
      if (near) return near.email;
    }
    return null;
  }
  return emails[0].email;
}

function parseInvoiceId(corpus = '', fileNames = []) {
  const text = String(corpus || '');
  const patterns = [
    /\b(?:invoice|inv|account)\s*(?:number|no\.?|#)?\s*[:#-]?\s*([A-Z]{0,8}-?[A-Z0-9]*\d[A-Z0-9-]*)\b/gi,
    /\b(INV-[A-Z0-9-]+)\b/gi,
    /\b([A-Z]{1,6}-\d{2,6}[A-Z0-9-]*)\b/g,
  ];
  for (const re of patterns) {
    let match;
    while ((match = re.exec(text))) {
      if (isPlausibleInvoiceId(match[1])) return match[1];
    }
  }
  // Filename fallback: Invoice_26026_a9b4.pdf → 26026
  for (const name of fileNames) {
    const base = String(name || '').replace(/\.[^.]+$/, '');
    const fromName =
      base.match(/\b(INV-[A-Z0-9-]+)\b/i) ||
      base.match(/(?:invoice|inv)[_#\s-]*([A-Z0-9][A-Z0-9-]{2,})/i) ||
      base.match(/_(\d{4,})(?:_|$)/);
    if (fromName && isPlausibleInvoiceId(fromName[1])) return fromName[1];
  }
  return null;
}

function parseAmountOwed(corpus = '') {
  const amountMatch =
    // Prefer labeled totals with a $ or cents so column headers like "TOTAL DUE" + invoice # don't win.
    corpus.match(
      /(?:balance\s*due|amount\s*due|total\s*due|amount\s*owed|total)\s*:?\s*\$\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i
    ) ||
    corpus.match(
      /(?:balance\s*due|amount\s*due|total\s*due|amount\s*owed)\s*:?\s*([0-9][0-9,]*(?:\.[0-9]{2}))\b/i
    ) ||
    corpus.match(/(?:USD|\$)\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i) ||
    corpus.match(/\b([0-9]{1,3}(?:,[0-9]{3})+\.[0-9]{2})\b/) ||
    corpus.match(/_\$([0-9]+(?:\.[0-9]{1,2})?)/);
  return amountMatch ? amountMatch[1] : null;
}

function parseBillToAddress(block = '') {
  const text = String(block || '');
  if (!text) return {};
  const postalMatch = text.match(/\b(\d{5}(?:-\d{4})?)\b/);
  const stateMatch =
    text.match(/\b([A-Z]{2})\b(?=\s*\d{5}(?:-\d{4})?\b)/) ||
    text.match(/\b(state|province)[:\s]+([A-Z]{2})\b/i);
  // Prefer "City, ST 12345" — avoid swallowing street suffixes into the city.
  const cityState =
    text.match(/\b([A-Z][a-z]+),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\b/) ||
    text.match(
      /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\b/
    );
  const streetMatch =
    text.match(/\b(p\.?\s*o\.?\s*box\s+\d+)\b/i) ||
    text.match(
      /\b(\d{1,6}\s+[A-Za-z0-9 .#'-]+(?:st|street|ave|avenue|rd|road|blvd|lane|ln|dr|drive|way|ct|court))\b/i
    );
  let debtorState =
    cityState ? cityState[2] : stateMatch ? stateMatch[stateMatch.length - 1] : null;
  let debtorPostalCode = cityState ? cityState[3] : postalMatch ? postalMatch[1] : null;
  // "Omaha, NE 68102" style already covered; also "Omaha NE 68102" / line "NE 68102"
  if (!debtorState || !debtorPostalCode) {
    const loose = text.match(/\b([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\b/);
    if (loose) {
      debtorState = debtorState || loose[1];
      debtorPostalCode = debtorPostalCode || loose[2];
    }
  }
  return {
    debtorStreet: streetMatch ? streetMatch[1] : null,
    debtorCity: cityState ? cityState[1].trim() : null,
    debtorState,
    debtorPostalCode,
  };
}

export function heuristicExtractEvidence({ fileNames = [], texts = [] } = {}) {
  const names = (fileNames || []).map(String);
  const textCorpus = (texts || [])
    .map((item) => `${item?.name || ''}\n${item?.text || ''}`)
    .join('\n')
    .trim();
  const corpus = `${names.join('\n')}\n${textCorpus}`.trim();

  // Parse party from invoice body first so filenames like BillTo_Atlas / Customer_Maple don't win.
  const billTo = parseBillToParty(textCorpus || corpus);
  const address = parseBillToAddress(billTo.block);
  const amountRaw = parseAmountOwed(corpus);
  const billToStartInCorpus =
    billTo.blockStart >= 0 && textCorpus
      ? corpus.indexOf(textCorpus) + billTo.blockStart
      : billTo.blockStart;
  const debtorEmail = parseDebtorEmail(corpus, billToStartInCorpus, billTo.company);
  const externalInvoiceId = parseInvoiceId(textCorpus || corpus, names);

  const companyFromName = names
    .map((name) => {
      const base = String(name || '').replace(/\.[^.]+$/, '');
      const parts = base.split(/[_\s-]+/).filter(Boolean);
      const meaningful = parts.filter(
        (part) => !FILENAME_JUNK.test(part) && !/^\d+$/.test(part)
      );
      const camel = meaningful.find((part) => /[a-z][A-Z]/.test(part));
      if (camel) return camel.replace(/([a-z])([A-Z])/g, '$1 $2');
      if (meaningful.length >= 2 && /corp|inc|llc|ltd|company|co/i.test(meaningful.join(' '))) {
        return meaningful.slice(0, 3).join(' ');
      }
      if (
        meaningful[0] &&
        meaningful[0].length > 2 &&
        !/invoice|receipt|statement|evidence/i.test(meaningful[0])
      ) {
        return meaningful[0].replace(/([a-z])([A-Z])/g, '$1 $2');
      }
      return null;
    })
    .find(Boolean);

  let disputeType = 'CORPORATE_BUSINESS_DEBT';
  if (/wage|employment|labor|wrongful/i.test(corpus)) disputeType = 'EMPLOYMENT_LABOR';
  else if (/lease|rent|landlord|tenant|property defect|construction/i.test(corpus)) {
    disputeType = 'REAL_ESTATE';
  } else if (/loan|credit card|overdraft|bank/i.test(corpus)) disputeType = 'BANKING_FINANCE';
  else if (/medical|hospital|healthcare|insurance claim/i.test(corpus)) disputeType = 'HEALTHCARE';

  // Prefer BILL TO party over filename guesses; filename is a last resort only.
  const debtorCompany = billTo.company || (!billTo.name ? companyFromName : null);
  return normalizeEvidenceExtraction(
    {
      debtorName: billTo.name,
      debtorCompany,
      debtorEmail,
      debtorStreet: address.debtorStreet,
      debtorCity: address.debtorCity,
      debtorState: address.debtorState,
      debtorPostalCode: address.debtorPostalCode,
      debtorCountry: 'US',
      amountOwed: amountRaw,
      externalInvoiceId,
      disputeType,
      summary: names.length
        ? `Parsed ${names.length} evidence file${names.length === 1 ? '' : 's'} with local AI assist.`
        : 'No evidence filenames provided.',
      confidence: {
        amountOwed: amountRaw ? 0.55 : 0,
        debtorCompany: debtorCompany ? (billTo.company ? 0.75 : 0.4) : 0,
        debtorName: billTo.name ? 0.7 : 0,
        debtorEmail: debtorEmail ? 0.6 : 0,
        externalInvoiceId: externalInvoiceId ? 0.5 : 0,
        disputeType: 0.35,
      },
    },
    'heuristic'
  );
}

async function readTextSnippet(file, maxChars = 8000) {
  if (!file) return '';
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  const isText =
    type.startsWith('text/') ||
    name.endsWith('.txt') ||
    name.endsWith('.csv') ||
    name.endsWith('.json') ||
    name.endsWith('.md');
  if (!isText) return '';
  try {
    const text = await file.text();
    return String(text || '').slice(0, maxChars);
  } catch {
    return '';
  }
}

async function readImageBase64(file, maxBytes = 1_500_000) {
  if (!file) return null;
  const type = String(file.type || '').toLowerCase();
  if (!type.startsWith('image/')) return null;
  if (Number(file.size) > maxBytes) return null;
  try {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return {
      name: file.name,
      mimeType: type || 'image/png',
      base64: btoa(binary),
    };
  } catch {
    return null;
  }
}

function isPdfFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
}

async function loadPdfjs() {
  const [pdfjsLib, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url').then((m) => m.default),
  ]);
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  return pdfjsLib;
}

function textFromPdfPage(content) {
  const items = Array.isArray(content?.items) ? content.items : [];
  return items
    .map((item) => String(item?.str || '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Read a PDF for extraction: pull selectable text (works offline / local assist)
 * and render page 1 as a compact JPEG for OpenAI vision.
 * Dynamic import keeps pdfjs-dist out of `node --test`; failures fall back to
 * filename-only, same as before.
 */
async function readPdfEvidence(file, { maxDim = 1400, maxPagesText = 2, maxChars = 8000 } = {}) {
  try {
    const pdfjsLib = await loadPdfjs();
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

    const pageCount = Math.min(Math.max(1, Number(pdf.numPages) || 1), maxPagesText);
    const textParts = [];
    for (let i = 1; i <= pageCount; i += 1) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = textFromPdfPage(content);
      if (pageText) textParts.push(pageText);
    }
    const text = textParts.join('\n').slice(0, maxChars);

    let image = null;
    try {
      const page = await pdf.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.min(2, maxDim / Math.max(base.width, base.height));
      const viewport = page.getViewport({ scale: Math.max(scale, 0.5) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const canvasContext = canvas.getContext('2d', { alpha: false });
      // pdfjs v4+ wants an explicit canvas; keep canvasContext for older builds.
      await page.render({ canvas, canvasContext, viewport }).promise;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      const base64 = dataUrl.split(',')[1] || '';
      if (base64) {
        image = { name: file.name, mimeType: 'image/jpeg', base64 };
      }
    } catch {
      image = null;
    }

    return { text: text || '', image };
  } catch {
    return { text: '', image: null };
  }
}

/** Build API payload from selected evidence File objects + names. */
export async function buildEvidenceExtractPayload({ fileNames = [], files = [] } = {}) {
  const names = (fileNames || []).map((n) => String(n || '').trim()).filter(Boolean);
  const list = Array.from(files || []).filter(Boolean);
  const texts = [];
  const images = [];
  for (const file of list) {
    if (isPdfFile(file)) {
      const pdf = await readPdfEvidence(file);
      if (pdf.text) texts.push({ name: file.name, text: pdf.text });
      if (pdf.image) images.push(pdf.image);
      continue;
    }
    const text = await readTextSnippet(file);
    if (text) texts.push({ name: file.name, text });
    const image = await readImageBase64(file);
    if (image) images.push(image);
  }
  return {
    fileNames: names.length ? names : list.map((file) => file.name).filter(Boolean),
    texts,
    images,
  };
}

/** True only for explicit missing-key signals — not generic network/API failures. */
export function looksLikeOpenAiNotConfigured(message = '', code = '') {
  if (code === 'OPENAI_NOT_CONFIGURED') return true;
  return /OPENAI_API_KEY|openai.*(not|isn.?t)\s+configured/i.test(String(message || ''));
}

/** Auth-protected probe — never invents "not configured" from transport failures. */
export async function fetchEvidenceAiStatus() {
  try {
    const data = await api.get('/server/client_api/ai/status');
    if (typeof data?.openaiConfigured === 'boolean') {
      return {
        openaiConfigured: data.openaiConfigured,
        model: data.model || null,
        provider: data.provider || null,
      };
    }
  } catch {
    /* status probe is best-effort */
  }
  return null;
}

/**
 * Call Worker extract endpoint. OpenAI owns evidence extraction — no local fallback.
 * Transport / auth / payload failures must not be labeled as "OpenAI isn’t configured".
 */
export async function extractEvidenceFromUploads({ fileNames = [], files = [] } = {}) {
  const payload = await buildEvidenceExtractPayload({ fileNames, files });
  if (!payload.fileNames.length) {
    const err = new Error('Add at least one evidence file before extracting.');
    err.code = 'NO_EVIDENCE';
    throw err;
  }
  try {
    const data = await api.post('/server/client_api/ai/extract-evidence', payload);
    const raw = data?.fields || data || {};
    const fields = normalizeEvidenceExtraction(raw, raw.source || data?.provider || 'openai');
    const usable = extractionHasAnyField(fields);
    const error = raw.error || data?.fallbackReason || null;
    const errorCode =
      raw.errorCode ||
      (looksLikeOpenAiNotConfigured(error, '') ? 'OPENAI_NOT_CONFIGURED' : null);
    const openaiConfigured =
      typeof data?.openaiConfigured === 'boolean'
        ? data.openaiConfigured
        : typeof raw.openaiConfigured === 'boolean'
          ? raw.openaiConfigured
          : errorCode === 'OPENAI_NOT_CONFIGURED'
            ? false
            : null;
    const usedOpenAI = Boolean(data?.usedOpenAI ?? raw.usedOpenAI) && usable;
    return {
      ...fields,
      model: data?.model || raw.model || null,
      openaiConfigured,
      usedOpenAI,
      source: 'openai',
      error,
      errorCode,
      fallbackReason: error,
    };
  } catch (err) {
    const message = err?.message || 'Extract API unavailable';
    let errorCode =
      err?.code ||
      err?.payload?.error?.code ||
      (looksLikeOpenAiNotConfigured(message, '') ? 'OPENAI_NOT_CONFIGURED' : 'EXTRACT_API_UNAVAILABLE');
    let openaiConfigured = errorCode === 'OPENAI_NOT_CONFIGURED' ? false : null;
    if (openaiConfigured == null) {
      const status = await fetchEvidenceAiStatus();
      if (status && typeof status.openaiConfigured === 'boolean') {
        openaiConfigured = status.openaiConfigured;
        if (!status.openaiConfigured) errorCode = 'OPENAI_NOT_CONFIGURED';
      }
    }
    return {
      ...normalizeEvidenceExtraction({}, 'openai'),
      usedOpenAI: false,
      openaiConfigured,
      source: 'openai',
      error: message,
      errorCode,
      fallbackReason: message,
    };
  }
}

/** Map extraction → Create Demand intake form patch (only non-empty). */
export function intakePatchFromExtraction(fields = {}, { overwrite = false } = {}) {
  const patch = {};
  const set = (key, value) => {
    if (value == null || value === '') return;
    patch[key] = value;
  };
  set('debtorName', fields.debtorName);
  set('debtorCompany', fields.debtorCompany);
  set('debtorEmail', fields.debtorEmail);
  set('debtorStreet', fields.debtorStreet);
  set('debtorCity', fields.debtorCity);
  set('debtorState', fields.debtorState);
  set('debtorPostalCode', fields.debtorPostalCode);
  set('debtorCountry', fields.debtorCountry);
  if (fields.amountOwed != null) set('amountOwed', formatExtractedAmount(fields.amountOwed));
  set('externalInvoiceId', fields.externalInvoiceId);
  set('disputeType', fields.disputeType);
  if (!overwrite) return patch;
  return {
    debtorName: fields.debtorName || '',
    debtorCompany: fields.debtorCompany || '',
    debtorEmail: fields.debtorEmail || '',
    debtorStreet: fields.debtorStreet || '',
    debtorCity: fields.debtorCity || '',
    debtorState: fields.debtorState || '',
    debtorPostalCode: fields.debtorPostalCode || '',
    debtorCountry: fields.debtorCountry || 'US',
    amountOwed: formatExtractedAmount(fields.amountOwed) || '',
    externalInvoiceId: fields.externalInvoiceId || '',
    disputeType: fields.disputeType || '',
  };
}

/** Map extraction → matter detail form patch. */
export function matterPatchFromExtraction(fields = {}) {
  const patch = {};
  const set = (key, value) => {
    if (value == null || value === '') return;
    patch[key] = value;
  };
  set('Debtor_Name', fields.debtorName);
  set('Debtor_Company', fields.debtorCompany);
  set('Debtor_Email', fields.debtorEmail);
  set('Debtor_Street', fields.debtorStreet);
  set('Debtor_City', fields.debtorCity);
  set('Debtor_State', fields.debtorState);
  set('Debtor_Postal_Code', fields.debtorPostalCode);
  set('Debtor_Country', fields.debtorCountry);
  if (fields.amountOwed != null) set('Amount_Owed', formatExtractedAmount(fields.amountOwed));
  set('External_Invoice_ID', fields.externalInvoiceId);
  set('Dispute_Type', fields.disputeType);
  return patch;
}

export function extractionHasAnyField(fields = {}) {
  return Boolean(
    fields.debtorName ||
      fields.debtorCompany ||
      fields.debtorEmail ||
      fields.debtorStreet ||
      fields.debtorCity ||
      fields.debtorState ||
      fields.debtorPostalCode ||
      fields.amountOwed != null ||
      fields.externalInvoiceId
  );
}
